import authService from './auth.service';
import { TicketCreatePayload, TicketDetails, TicketEmail, TicketItem, TicketListParams, TicketListResponse, TicketRelationDirection, TicketRelationItem, TicketThreadTreeResponse } from '../types/ticket.types';
import type { EmailThreadTreeItem } from '../types/email.types';
import { TaskCreatePayload, TaskListResponse } from '../types/task.types';
import type { TicketLinkedAgreement, LinkAgreementPayload } from '../types/ticket-agreement.types';

const API_URL = '/api/v1';

class TicketService {
  buildQuery(params: Record<string, string | number | boolean | undefined>) {
    const query = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join('&');
    return query ? `?${query}` : '';
  }

  async getTickets(params: TicketListParams): Promise<TicketListResponse> {
    const {
      page = 1,
      page_size = 20,
      ticket_id,
      status_id,
      assigned_to_me,
      is_active,
      customer_id,
      type_id,
      priority_id,
      owner_id,
      task_assignee_id,
      stage_code,
      is_overdue,
      account_id,
      created_from,
      created_to,
      task_completed_from,
      task_completed_to,
      customer_identification_no,
      attachment_filename,
      from_address,
    } = params;

    const query = this.buildQuery({
      page,
      page_size,
      ticket_id,
      status_id,
      assigned_to_me,
      is_active,
      customer_id,
      type_id,
      priority_id,
      owner_id,
      task_assignee_id: task_assignee_id ?? undefined,
      stage_code,
      is_overdue,
      account_id,
      created_from,
      created_to,
      task_completed_from,
      task_completed_to,
      customer_identification_no,
      attachment_filename,
      from_address,
    });
    // FastAPI маршрут без завершающего слэша — не добавляем '/'
    const res = await authService.fetchWithAuth(`${API_URL}/tickets${query}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить тикеты');
    }
    const data = (await res.json()) as TicketListResponse;
    // Страховка по структуре
    return {
      items: Array.isArray((data as any).items) ? data.items : [],
      total: Number((data as any).total ?? 0),
      page: Number((data as any).page ?? page),
      page_size: Number((data as any).page_size ?? page_size),
    };
  }

  async getTicket(id: number): Promise<TicketDetails> {
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${id}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить тикет');
    }
    const data = (await res.json()) as TicketDetails;
    // Подстрахуем отсутствие массивов
    return {
      ...data,
      labels: Array.isArray((data as any).labels) ? data.labels : [],
    };
  }

  /**
   * Загрузить новые тикеты по "бакету" (earlier | day_before_yesterday | yesterday | today)
   */
  async getNewTicketsByBucket(params: { bucket: 'earlier' | 'day_before_yesterday' | 'yesterday' | 'today'; page?: number; page_size?: number }): Promise<TicketListResponse> {
    const { bucket, page = 1, page_size = 20 } = params;
    const query = this.buildQuery({ bucket, page, page_size });
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/new-tickets${query}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить тикеты по бакету');
    }
    const data = (await res.json()) as TicketListResponse;
    return {
      items: Array.isArray((data as any).items) ? data.items : [],
      total: Number((data as any).total ?? 0),
      page: Number((data as any).page ?? page),
      page_size: Number((data as any).page_size ?? page_size),
    };
  }

  /**
   * Назначенные на меня по дедлайну (бакеты: overdue | today | tomorrow | day_after_tomorrow | later)
   */
  async getMyAssignedByDeadline(params: { bucket: 'overdue' | 'today' | 'tomorrow' | 'day_after_tomorrow' | 'later'; page?: number; page_size?: number }): Promise<TicketListResponse> {
    const { bucket, page = 1, page_size = 20 } = params;
    const query = this.buildQuery({ bucket, page, page_size });
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/my-assigned-by-deadline${query}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить назначенные тикеты по дедлайну');
    }
    const data = (await res.json()) as TicketListResponse;
    return {
      items: Array.isArray((data as any).items) ? data.items : [],
      total: Number((data as any).total ?? 0),
      page: Number((data as any).page ?? page),
      page_size: Number((data as any).page_size ?? page_size),
    };
  }

  /**
   * Активные тикеты (исключая закрытую стадию по умолчанию)
   */
  async getActiveTickets(params: { page?: number; page_size?: number; exclude_stage3?: boolean } = {}): Promise<TicketListResponse> {
    const { page = 1, page_size = 20, exclude_stage3 = true } = params;
    const query = this.buildQuery({ page, page_size, exclude_stage3: exclude_stage3 ? 1 : 0 });
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/active-tickets${query}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить активные тикеты');
    }
    const data = (await res.json()) as TicketListResponse;
    return {
      items: Array.isArray((data as any).items) ? data.items : [],
      total: Number((data as any).total ?? 0),
      page: Number((data as any).page ?? page),
      page_size: Number((data as any).page_size ?? page_size),
    };
  }

  async searchTickets(params: {
    limit?: number;
    offset?: number;
    order_by?: string[];
    [key: string]: unknown;
  }): Promise<{ items: TicketItem[]; total: number }> {
    const { limit = 20, offset = 0, order_by, ...filters } = params;

    const filtersBody: Record<string, unknown> = {};
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'object' && !Array.isArray(value)) {
        const r = value as { from?: unknown; to?: unknown };
        if (r.from === undefined && r.to === undefined) return;
      }
      filtersBody[key] = value;
    });

    const requestBody: Record<string, unknown> = {
      filters: filtersBody,
      limit,
      offset,
    };
    if (order_by && order_by.length > 0) {
      requestBody.order_by = order_by;
    }

    const res = await authService.fetchWithAuth(`${API_URL}/tickets/search`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось выполнить поиск тикетов');
    }
    const data = await res.json();
    return {
      items: Array.isArray(data.items) ? data.items : [],
      total: Number(data.total ?? 0),
    };
  }

  async createTicket(payload: TicketCreatePayload): Promise<TicketDetails> {
    const res = await authService.fetchWithAuth(`${API_URL}/tickets`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать тикет');
    }
    const data = (await res.json()) as TicketDetails;
    return {
      ...data,
      labels: Array.isArray((data as any).labels) ? data.labels : [],
    };
  }

  async getTicketEmail(id: number): Promise<TicketEmail> {
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${id}/email`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить содержимое email');
    }
    const data = (await res.json()) as TicketEmail;
    return {
      ...data,
      attachments: Array.isArray((data as any).attachments) ? data.attachments : [],
    };
  }

  /**
   * Список связей тикета
   * GET /api/v1/tickets/{id}/relations?direction=outgoing|incoming|all
   */
  async listRelations(id: number, params: { direction?: TicketRelationDirection } = {}): Promise<TicketRelationItem[]> {
    const q = this.buildQuery({ direction: params.direction });
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${id}/relations${q}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить связанные тикеты');
    }
    const data = await res.json();
    if (!Array.isArray(data)) return [];
    return data.map((r: any) => ({
      id: Number(r.id),
      relation_type: String(r.relation_type || ''),
      direction: (r.direction === 'incoming' ? 'incoming' : 'outgoing') as 'incoming' | 'outgoing',
      target: {
        id: Number(r?.target?.id),
        title: String(r?.target?.title || ''),
        status_id: r?.target?.status_id ?? null,
        status_name: r?.target?.status_name ?? null,
        stage_code: r?.target?.stage_code ?? null,
        stage_name: r?.target?.stage_name ?? null,
        created_at: r?.target?.created_at ?? null,
      },
    }));
  }

  /** Частичное обновление тикета (например, статус) */
  async updateTicketPartial(
    id: number,
    data: Partial<{ status_id: number; priority_id: number; sla_id: number; type_id: number; customer_id: number }>
  ): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось обновить тикет');
    }
  }

  /** Привязка клиента к тикету с указанием типа привязки */
  async linkCustomerToTicket(
    ticketId: number,
    customerId: number,
    linkType: 'manual_no_email' | 'manual_with_email',
    comment?: string
  ): Promise<void> {
    const payload: { customer_id: number; link_type: string; comment?: string } = {
      customer_id: customerId,
      link_type: linkType,
    };
    if (comment) {
      payload.comment = comment;
    }
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${ticketId}/customers`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось привязать клиента к тикету');
    }
  }

  /** Отвязка клиента от тикета */
  async unlinkCustomerFromTicket(ticketId: number, linkId: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${ticketId}/customers/${linkId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось отвязать клиента от тикета');
    }
  }

  async replyToTicketEmail(
    id: number,
    payload: { to: string; subject: string; body_html: string; files?: File[] }
  ): Promise<void> {
    const form = new FormData();
    form.append('to', payload.to || '');
    form.append('subject', payload.subject || '');
    form.append('body_html', payload.body_html || '');
    (payload.files || []).forEach((f) => form.append('files', f, f.name));

    const res = await authService.fetchWithAuthForm(`${API_URL}/tickets/${id}/reply`, {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось отправить ответ по email');
    }
  }

  /**
   * Назначить/переназначить ответственного на тикете
   * POST /api/v1/tickets/{id}:reassign-owner
   */
  async reassignOwner(
    id: number,
    payload: { to_user_id: number; comment?: string | null; reason?: string | null }
  ): Promise<boolean> {
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${id}:reassign-owner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to_user_id: payload.to_user_id,
        // отправляем оба поля: comment и reason
        comment: (payload.comment ?? null),
        reason: (payload.reason ?? payload.comment ?? null),
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось изменить ответственного');
    }
    const data = await res.json().catch(() => ({ success: true }));
    return Boolean((data as any).success ?? true);
  }

  /**
   * Старт работы ответственного (start-owner)
   * POST /api/v1/tickets/{id}:start-owner
   */
  async startOwner(id: number, payload: { comment?: string | null } = {}): Promise<boolean> {
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${id}:start-owner`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment: payload.comment ?? null }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось начать работу по тикету');
    }
    const data = await res.json().catch(() => ({ success: true }));
    return Boolean((data as any).success ?? true);
  }
  /**
   * Список задач тикета
   */
  async getTicketTasks(id: number, params: { page?: number; page_size?: number } = {}): Promise<TaskListResponse> {
    const { page = 1, page_size = 20 } = params;
    const query = this.buildQuery({ page, page_size });
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${id}/tasks${query}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить задачи тикета');
    }
    const data = await res.json().catch(() => ({}));
    const items = Array.isArray((data as any).items)
      ? (data as any).items.map((t: any) => ({
          id: Number(t.id),
          ticket_id: Number(t.ticket_id),
          title: String(t.title || ''),
          description: t.description ?? null,
          priority: t.priority != null ? Number(t.priority) : null,
          status: String(t.status || 'open'),
          due_at: t.due_at ?? null,
          assignee_id: t.assignee_id != null ? Number(t.assignee_id) : null,
          assignee_full_name: t.assignee_full_name ?? null,
          task_type_id: t.task_type_id != null ? Number(t.task_type_id) : null,
          task_type_name: t.task_type_name ?? null,
          assigned_at: t.assigned_at ?? null,
          assignee_started_at: t.assignee_started_at ?? null,
          completed_at: t.completed_at ?? null,
          rejected_at: t.rejected_at ?? null,
          rejected_by: t.rejected_by != null ? Number(t.rejected_by) : null,
          rejected_reason: t.rejected_reason ?? null,
          rejected_by_full_name: t.rejected_by_full_name ?? null,
          order_index: t.order_index != null ? Number(t.order_index) : null,
          created_at: String(t.created_at || ''),
          updated_at: String(t.updated_at || ''),
          is_overdue: Boolean(t.is_overdue),
        }))
      : [];

    return {
      items,
      total: Number((data as any).total ?? items.length),
      page: Number((data as any).page ?? page),
      page_size: Number((data as any).page_size ?? page_size),
    };
  }

  /** Создать задачу в тикете */
  async createTicketTask(id: number, payload: TaskCreatePayload) {
    // Возвращаем передачу описания как description
    const body = {
      title: payload.title,
      priority: payload.priority ?? null,
      due_at: payload.due_at ?? null,
      assignee_id: payload.assignee_id ?? null,
      description: payload.description ?? null,
      task_type_id: payload.task_type_id ?? null,
    } as const;
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${id}/tasks`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось создать задачу');
    }
    const t = await res.json();
    return {
      id: Number(t.id),
      ticket_id: Number(t.ticket_id ?? id),
      title: String(t.title || ''),
      description: t.description ?? null,
      priority: t.priority != null ? Number(t.priority) : null,
      status: String(t.status || 'open'),
      due_at: t.due_at ?? null,
      assignee_id: t.assignee_id != null ? Number(t.assignee_id) : null,
      assignee_full_name: t.assignee_full_name ?? null,
      task_type_id: t.task_type_id != null ? Number(t.task_type_id) : null,
      task_type_name: t.task_type_name ?? null,
      assigned_at: t.assigned_at ?? null,
      assignee_started_at: t.assignee_started_at ?? null,
      completed_at: t.completed_at ?? null,
      order_index: t.order_index != null ? Number(t.order_index) : null,
      created_at: String(t.created_at || ''),
      updated_at: String(t.updated_at || ''),
      is_overdue: Boolean(t.is_overdue),
    };
  }

  /**
   * Запустить выполнение задачи
   * POST /api/v1/tasks/{taskId}:start
   */
  async startTask(taskId: number, payload: { comment?: string | null } = {}): Promise<boolean> {
    const res = await authService.fetchWithAuth(`${API_URL}/tasks/${taskId}:start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ comment: payload.comment ?? null }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось начать задачу');
    }
    const data = await res.json().catch(() => ({ success: true }));
    return Boolean((data as any).success ?? true);
  }

  /**
   * Завершить задачу
   * POST /api/v1/tasks/{taskId}:complete
   */
  async completeTask(taskId: number, payload: { comment: string; close_ticket_if_no_active?: boolean }): Promise<boolean> {
    const res = await authService.fetchWithAuth(`${API_URL}/tasks/${taskId}:complete`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        close_ticket_if_no_active: payload.close_ticket_if_no_active ?? false,
        comment: payload.comment,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось завершить задачу');
    }
    const data = await res.json().catch(() => ({ success: true }));
    return Boolean((data as any).success ?? true);
  }

  /**
   * Дерево переписки и связанных тикетов для тикета
   * GET /api/v1/tickets/{id}/thread-tree
   */
  async getTicketThreadTree(
    ticketId: number,
    params: { page?: number; page_size?: number; order?: 'asc' | 'desc'; only_with_ticket?: boolean; include_ticket_relations?: boolean } = {}
  ): Promise<TicketThreadTreeResponse> {
    const { page = 1, page_size = 50, order = 'asc', only_with_ticket = false, include_ticket_relations = true } = params;
    const query = this.buildQuery({ page, page_size, order, only_with_ticket: only_with_ticket ? 'true' : 'false', include_ticket_relations: include_ticket_relations ? 'true' : 'false' });
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${ticketId}/thread-tree${query}`);
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось загрузить дерево переписки');
    }
    const data = await res.json();
    const items: EmailThreadTreeItem[] = Array.isArray((data as any)?.items) ? (data as any).items : [];
    return {
      base_ticket_id: Number((data as any)?.base_ticket_id ?? ticketId),
      base_email_id: (data as any)?.base_email_id ?? null,
      root_id: Number((data as any)?.root_id ?? 0),
      items,
      total: Number((data as any)?.total ?? items.length),
      page: Number((data as any)?.page ?? page),
      page_size: Number((data as any)?.page_size ?? page_size),
      order: ((data as any)?.order === 'desc' ? 'desc' : 'asc'),
      related_tickets: Array.isArray((data as any)?.related_tickets) ? (data as any).related_tickets : undefined,
      relation_edges: Array.isArray((data as any)?.relation_edges) ? (data as any).relation_edges : undefined,
    } as TicketThreadTreeResponse;
  }

  /**
   * Переназначить исполнителя задачи
   * POST /api/v1/tasks/{taskId}:reassign
   */
  async reassignTask(taskId: number, payload: { to_user_id: number; comment?: string | null; reason?: string | null }): Promise<boolean> {
    const res = await authService.fetchWithAuth(`${API_URL}/tasks/${taskId}:reassign`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to_user_id: payload.to_user_id,
        reason: payload.reason ?? payload.comment ?? null,
        comment: payload.comment ?? null,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось переназначить исполнителя');
    }
    const data = await res.json().catch(() => ({ success: true }));
    return Boolean((data as any).success ?? true);
  }

  /**
   * Отклонить задачу
   * POST /api/v1/tasks/{taskId}:reject
   */
  async rejectTask(taskId: number, payload: { to_user_id?: number; comment: string; reason: string }): Promise<boolean> {
    const body: any = { reason: payload.reason, comment: payload.comment };
    if (payload.to_user_id != null) body.to_user_id = payload.to_user_id;
    const res = await authService.fetchWithAuth(`${API_URL}/tasks/${taskId}:reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось отклонить задачу');
    }
    const data = await res.json().catch(() => ({ success: true }));
    return Boolean((data as any).success ?? true);
  }

  /**
   * Распределить задачу между пользователями
   * POST /api/v1/tasks/{taskId}:distribute
   */
  async distributeTask(taskId: number, payload: { user_ids: number[] }): Promise<boolean> {
    const res = await authService.fetchWithAuth(`${API_URL}/tasks/${taskId}:distribute`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_ids: payload.user_ids }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось распределить задачу');
    }
    const data = await res.json().catch(() => ({ success: true }));
    return Boolean((data as any).success ?? true);
  }

  /**
   * Привязка договора к тикету
   * POST /api/v1/tickets/{ticketId}/agreements
   * Permission: actions:use:tickets:agreement:set:any
   */
  async linkAgreementToTicket(
    ticketId: number,
    payload: LinkAgreementPayload
  ): Promise<TicketLinkedAgreement> {
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${ticketId}/agreements`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        agreement_id: payload.agreement_id,
        auto_link_customer: payload.auto_link_customer ?? true,
        comment: payload.comment ?? null,
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось привязать договор к тикету');
    }
    const data = await res.json();
    return {
      id: Number(data.id),
      ticket_id: Number(data.ticket_id),
      agreement_id: Number(data.agreement_id),
      agreement_external_id: Number(data.agreement_external_id),
      agreement_number: String(data.agreement_number || ''),
      customer_id: Number(data.customer_id),
      customer_full_name: String(data.customer_full_name || ''),
      customer_identification_no: String(data.customer_identification_no || ''),
      link_type: data.link_type || 'manual',
      linked_by_user_id: Number(data.linked_by_user_id),
      linked_by_full_name: String(data.linked_by_full_name || ''),
      linked_at: String(data.linked_at || ''),
      comment: data.comment ?? null,
    };
  }

  /**
   * Отвязка договора от тикета
   * DELETE /api/v1/tickets/{ticketId}/agreements/{linkId}
   * Permission: actions:use:tickets:agreement:unlink:any
   */
  async unlinkAgreementFromTicket(ticketId: number, linkId: number): Promise<void> {
    const res = await authService.fetchWithAuth(`${API_URL}/tickets/${ticketId}/agreements/${linkId}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.detail || 'Не удалось отвязать договор от тикета');
    }
  }
}

export default new TicketService();
